import { ProtocolVersion } from '@uniswap/client-pools/dist/pools/v1/types_pb'
import { Currency, CurrencyAmount, Token } from '@uniswap/sdk-core'
import {
  CreateLPPositionRequest,
  IncreaseLPPositionRequest,
  MigrateLPPositionRequest,
} from 'uniswap/src/data/tradingApi/__generated__'
import type { GaugeType } from 'uniswap/src/features/transactions/liquidity/steps/claimPositionRewards'
import {
  PermitMethod,
  PermitTransaction,
  PermitTypedData,
} from 'uniswap/src/features/transactions/swap/types/swapTxAndGasInfo'
import { ValidatedTransactionRequest } from 'uniswap/src/features/transactions/swap/utils/trade'
import type { Abi } from 'viem'

export enum LiquidityTransactionType {
  Create = 'create',
  Increase = 'increase',
  Decrease = 'decrease',
  Migrate = 'migrate',
  Collect = 'collect',
  ClaimIncentives = 'claim_incentives',
  Subscribe = 'subscribe',
}

export interface LiquidityAction {
  type: LiquidityTransactionType
  currency0Amount: CurrencyAmount<Currency>
  currency1Amount: CurrencyAmount<Currency>
  liquidityToken?: Token
}

export type LiquidityTxAndGasInfo =
  | IncreasePositionTxAndGasInfo
  | DecreasePositionTxAndGasInfo
  | CreatePositionTxAndGasInfo
  | MigrateV3PositionTxAndGasInfo
  | CollectFeesTxAndGasInfo
  | ClaimIncentivesTxAndGasInfo
  | SubscribeTxAndGasInfo
export type ValidatedLiquidityTxContext =
  | ValidatedIncreasePositionTxAndGasInfo
  | ValidatedDecreasePositionTxAndGasInfo
  | ValidatedCreatePositionTxAndGasInfo
  | ValidatedMigrateV3PositionTxAndGasInfo
  | ValidatedCollectFeesTxAndGasInfo
  | ClaimIncentivesTxAndGasInfo
  | SubscribeTxAndGasInfo

export function isValidLiquidityTxContext(
  liquidityTxContext: LiquidityTxAndGasInfo | unknown,
): liquidityTxContext is ValidatedLiquidityTxContext {
  // Validation fn prevents/future-proofs typeguard against illicit casts
  return validateLiquidityTxContext(liquidityTxContext) !== undefined
}

interface BaseLiquidityTxAndGasInfo {
  protocolVersion: ProtocolVersion
  action: LiquidityAction
  approveToken0Request: ValidatedTransactionRequest | undefined
  approveToken1Request: ValidatedTransactionRequest | undefined
  approvePositionTokenRequest: ValidatedTransactionRequest | undefined
  permit: PermitTypedData | undefined
  token0PermitTransaction: ValidatedTransactionRequest | undefined
  token1PermitTransaction: ValidatedTransactionRequest | undefined
  positionTokenPermitTransaction: ValidatedTransactionRequest | undefined
  revokeToken0Request: ValidatedTransactionRequest | undefined
  revokeToken1Request: ValidatedTransactionRequest | undefined
  txRequest: ValidatedTransactionRequest | undefined
}

export interface IncreasePositionTxAndGasInfo extends BaseLiquidityTxAndGasInfo {
  type: LiquidityTransactionType.Increase
  unsigned: boolean
  increasePositionRequestArgs: IncreaseLPPositionRequest | undefined
}

export interface DecreasePositionTxAndGasInfo extends BaseLiquidityTxAndGasInfo {
  type: LiquidityTransactionType.Decrease
}

export interface CreatePositionTxAndGasInfo extends BaseLiquidityTxAndGasInfo {
  type: LiquidityTransactionType.Create
  unsigned: boolean
  createPositionRequestArgs: CreateLPPositionRequest | undefined
  initializePoolRequest: ValidatedTransactionRequest | undefined
}

export interface MigrateV3PositionTxAndGasInfo extends BaseLiquidityTxAndGasInfo {
  type: LiquidityTransactionType.Migrate
  migratePositionRequestArgs: MigrateLPPositionRequest | undefined
  unsigned: boolean
}

export interface CollectFeesTxAndGasInfo {
  type: LiquidityTransactionType.Collect
  protocolVersion: ProtocolVersion
  action: LiquidityAction
  txRequest: ValidatedTransactionRequest | undefined
}

export interface ClaimIncentiveRequest {
  txRequest: ValidatedTransactionRequest
  rewardSymbol?: string
  gauge?: GaugeType
  rewardTokenAddress?: `0x${string}`
  isBmx?: boolean
}

export interface StakeAfterClaimAccumulator {
  totalClaimed: bigint
}

export interface StakeAfterClaimConfig {
  accumulator: StakeAfterClaimAccumulator
  stakingRouterAddress: `0x${string}`
  stakingRouterAbi: Abi
  accountAddress: `0x${string}`
  chainId: number
  bmxToken: Token
  stakingSpenderAddress: `0x${string}`
  estimatedClaimAmount: bigint
}

export interface ClaimIncentivesTxAndGasInfo {
  type: LiquidityTransactionType.ClaimIncentives
  protocolVersion: ProtocolVersion
  action: LiquidityAction
  claimRequests: ClaimIncentiveRequest[]
  stakeAfterClaim?: StakeAfterClaimConfig
}

export interface SubscribeTxAndGasInfo {
  type: LiquidityTransactionType.Subscribe
  protocolVersion: ProtocolVersion
  action: LiquidityAction
  txRequest: ValidatedTransactionRequest
}

export type ValidatedIncreasePositionTxAndGasInfo = Required<IncreasePositionTxAndGasInfo> &
  (
    | {
        unsigned: true
        permit: PermitTypedData
        txRequest: undefined
      }
    | {
        unsigned: false
        permit: PermitTransaction | undefined
        txRequest: ValidatedTransactionRequest
      }
  )

export type ValidatedDecreasePositionTxAndGasInfo = Required<DecreasePositionTxAndGasInfo> & {
  txRequest: ValidatedTransactionRequest
}

export type ValidatedCreatePositionTxAndGasInfo = Required<CreatePositionTxAndGasInfo> &
  (
    | {
        unsigned: true
        permit: PermitTypedData
        txRequest: undefined
      }
    | {
        unsigned: false
        permit: PermitTransaction | undefined
        txRequest: ValidatedTransactionRequest | undefined
      }
  )

export type ValidatedMigrateV3PositionTxAndGasInfo = Required<MigrateV3PositionTxAndGasInfo> &
  (
    | {
        unsigned: true
        permit: PermitTypedData
        txRequest: undefined
      }
    | {
        unsigned: false
        permit: PermitTransaction | undefined
        txRequest: ValidatedTransactionRequest
      }
  )

export type ValidatedCollectFeesTxAndGasInfo = CollectFeesTxAndGasInfo & {
  txRequest: ValidatedTransactionRequest
}

function validateLiquidityTxContext(
  liquidityTxContext: LiquidityTxAndGasInfo | unknown,
): ValidatedLiquidityTxContext | undefined {
  if (!isLiquidityTx(liquidityTxContext)) {
    return undefined
  }

  if (liquidityTxContext.type === LiquidityTransactionType.Collect) {
    if (liquidityTxContext.txRequest) {
      return { ...liquidityTxContext, txRequest: liquidityTxContext.txRequest }
    }

    return undefined
  }

  if (liquidityTxContext.type === LiquidityTransactionType.ClaimIncentives) {
    const context = liquidityTxContext as ClaimIncentivesTxAndGasInfo
    if (
      Array.isArray(context.claimRequests) &&
      context.claimRequests.length > 0 &&
      context.claimRequests.every((req) => req && typeof req === 'object' && 'txRequest' in req)
    ) {
      return context
    }
    return undefined
  }

  if (liquidityTxContext.type === LiquidityTransactionType.Subscribe) {
    if ((liquidityTxContext as SubscribeTxAndGasInfo).txRequest) {
      return liquidityTxContext as SubscribeTxAndGasInfo
    }
    return undefined
  }

  if (liquidityTxContext.action) {
    const { action, txRequest, permit } = liquidityTxContext

    switch (liquidityTxContext.type) {
      case LiquidityTransactionType.Create: {
        const unsigned = Boolean(liquidityTxContext.unsigned)
        if (unsigned) {
          if (!permit || permit.method !== PermitMethod.TypedData) {
            return undefined
          }
          return { ...liquidityTxContext, action, unsigned, txRequest: undefined, permit }
        }

        if (txRequest || liquidityTxContext.initializePoolRequest) {
          return { ...liquidityTxContext, action, unsigned, txRequest, permit: undefined }
        }
        break
      }
      case LiquidityTransactionType.Increase:
      case LiquidityTransactionType.Migrate: {
        const unsigned = Boolean(liquidityTxContext.unsigned)
        if (unsigned) {
          if (!permit || permit.method !== PermitMethod.TypedData) {
            return undefined
          }
          return { ...liquidityTxContext, action, unsigned, txRequest: undefined, permit }
        }

        if (txRequest) {
          return { ...liquidityTxContext, action, unsigned, txRequest, permit: undefined }
        }
        break
      }
      default: {
        if (txRequest) {
          return { ...liquidityTxContext, action, txRequest, permit: undefined }
        }
        break
      }
    }
  }

  return undefined
}

function isLiquidityTx(liquidityTxContext: unknown): liquidityTxContext is LiquidityTxAndGasInfo {
  return (
    typeof liquidityTxContext === 'object' &&
    liquidityTxContext !== null &&
    'action' in liquidityTxContext &&
    'protocolVersion' in liquidityTxContext
  )
}
